import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { photoFileUrl } from "@/lib/gallery";
import { formatApiError, fmtDate } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Upload, Loader2, Trash2, ImageOff, X, CheckCircle2, AlertCircle,
  FolderTree, Folder, FolderOpen, Plus, Save, Share2, Sparkles, Zap, Feather, Gem,
  QrCode, Download, Stamp,
} from "lucide-react";
import { compressImage, COMPRESSION_PRESETS, humanBytes } from "@/lib/imageCompress";
import { API } from "@/lib/api";
import GalleryUploadQueue from "@/components/GalleryUploadQueue";
import { toast } from "sonner";

const ALLOWED = /image\/(jpeg|jpg|png|webp|heic|heif)/i;

export default function GalleryDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer"; // iter120: admin + manager + coordinator all get write access
  const isAdmin = user?.role === "admin";

  // Main event (top-level) doc + selected node id (main or sub)
  const [mainEvent, setMainEvent] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null); // full event doc for selected node
  const [loading, setLoading] = useState(true);
  const [uploadResults, setUploadResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [showSubForm, setShowSubForm] = useState(false);
  const [subForm, setSubForm] = useState({ name: "", event_date: "" });
  const [subErr, setSubErr] = useState("");
  const [subSaving, setSubSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadPreset, setUploadPreset] = useState(() => localStorage.getItem("kahani_upload_preset") || "balanced");
  // iter198 — Studio-wide watermark default (drives the "Inherit" label).
  const [studioWatermark, setStudioWatermark] = useState(null);
  const [savingWatermark, setSavingWatermark] = useState(false);
  // iter168 — Per-file queue upload state. `queueFiles` seeds the modal
  // once when a batch is picked; `queueOpen` controls modal visibility.
  const [queueFiles, setQueueFiles] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false);
  // iter138 — face-index reliability admin panel
  const [indexStatus, setIndexStatus] = useState(null);
  const [indexLog, setIndexLog] = useState(null);  // iter139 — {job, logs[]}
  const [indexPolling, setIndexPolling] = useState(false);
  const [fullReindexing, setFullReindexing] = useState(false);
  const [showReindexConfirm, setShowReindexConfirm] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Load: fetch the URL event. If it's a sub-event, also fetch its parent so we
   * can render the full tree. If it's a main event, use it as-is.
   */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gallery/events/${eventId}`);
      const isSub = !!(data.parent_id || "").trim();
      const mainId = isSub ? data.parent_id : data.id;
      const main = isSub ? (await api.get(`/gallery/events/${mainId}`)).data : data;
      setMainEvent(main);
      // Default selection: the node in the URL
      setSelectedId(data.id);
      setSelectedNode(data);
    } catch (e) {
      logger.debug("gallery detail load failed:", e?.message);
      if (e?.response?.status === 404) navigate("/gallery");
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  // iter198 — Load studio-wide watermark default (public endpoint, no auth).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/media-settings");
        setStudioWatermark(data.watermark || null);
      } catch { /* soft-fail: card just hides the "inherit" hint */ }
    })();
  }, []);

  // iter198 — Save the gallery's watermark override (null=inherit, bool=force).
  const saveGalleryWatermark = async (value) => {
    if (!mainEvent) return;
    setSavingWatermark(true);
    try {
      const { data } = await api.patch(`/gallery/events/${mainEvent.id}`, { watermark_enabled: value });
      setMainEvent((prev) => ({ ...(prev || {}), ...data }));
      toast.success(
        value === null
          ? "Using studio default watermark"
          : value
            ? "Watermark ON for this gallery"
            : "Watermark OFF for this gallery",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingWatermark(false);
    }
  };

  /** Fetch just the selected node's photos + metadata without reloading the whole tree. */
  const refreshSelected = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/gallery/events/${id}`);
      setSelectedNode(data);
      // Also refresh main-event tree stats so sub-event photo counts update
      const mainId = (data.parent_id || "").trim() || data.id;
      const { data: freshMain } = await api.get(`/gallery/events/${mainId}`);
      setMainEvent(freshMain);
    } catch (e) {
      logger.debug("refresh selected failed:", e?.message);
    }
  }, []);

  const selectNode = async (id) => {
    if (id === selectedId) return;
    setSelectedId(id);
    setUploadResults(null);
    try {
      const { data } = await api.get(`/gallery/events/${id}`);
      setSelectedNode(data);
    } catch (e) {
      logger.debug("switch node failed:", e?.message);
    }
  };

  const uploadFiles = async (fileList) => {
    if (!selectedId) return;
    const raw = Array.from(fileList).filter((f) => ALLOWED.test(f.type || ""));
    const rejected = Array.from(fileList).length - raw.length;
    if (raw.length === 0) {
      setUploadResults({ uploaded: [], errors: [{ filename: "", error: `${rejected} file(s) skipped — unsupported type.` }] });
      return;
    }
    // iter168 — hand the batch over to the per-file queue modal. Every
    // file gets its own POST + progress + retry semantics inside there.
    // Compression + AI indexing happen exactly as before (compression is
    // now per-file inside the queue; indexing is server-side and untouched).
    setUploadResults(rejected > 0
      ? { uploaded: [], errors: [{ filename: "", error: `${rejected} file(s) skipped — unsupported type.` }] }
      : null);
    setQueueFiles(raw);
    setQueueOpen(true);
  };

  const changeUploadPreset = (key) => {
    setUploadPreset(key);
    try {
      localStorage.setItem("kahani_upload_preset", key);
    } catch (e) {
      // Quota / disabled storage — the preference just doesn't persist. Fine.
      logger.warn("preset persist failed:", e?.message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canWrite || queueOpen) return;
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const onFileInputChange = (e) => {
    if (!e.target.files?.length) return;
    uploadFiles(e.target.files);
    e.target.value = "";
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm(`Delete "${photo.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/gallery/photos/${photo.id}`);
      refreshSelected(selectedId);
      if (lightbox?.id === photo.id) setLightbox(null);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteMainEvent = async () => {
    if (!mainEvent) return;
    const subLabel = mainEvent.sub_event_count
      ? `, ${mainEvent.sub_event_count} sub-event${mainEvent.sub_event_count === 1 ? "" : "s"}`
      : "";
    if (!window.confirm(`Delete "${mainEvent.name}" and ALL its ${mainEvent.photo_count} photo${mainEvent.photo_count === 1 ? "" : "s"}${subLabel}? This cannot be undone.`)) return;
    try {
      await api.delete(`/gallery/events/${mainEvent.id}`);
      navigate("/gallery");
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteSubEvent = async (sub) => {
    if (!window.confirm(`Delete sub-event "${sub.name}" and its ${sub.photo_count} photo${sub.photo_count === 1 ? "" : "s"}?`)) return;
    try {
      await api.delete(`/gallery/events/${sub.id}`);
      // If the deleted sub was selected, switch back to the main event
      if (selectedId === sub.id) {
        setSelectedId(mainEvent.id);
        const { data } = await api.get(`/gallery/events/${mainEvent.id}`);
        setSelectedNode(data);
        setMainEvent(data);
      } else {
        const { data: freshMain } = await api.get(`/gallery/events/${mainEvent.id}`);
        setMainEvent(freshMain);
      }
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const createSubEvent = async (e) => {
    e.preventDefault();
    setSubErr("");
    setSubSaving(true);
    try {
      const { data: newSub } = await api.post("/gallery/events", {
        ...subForm,
        parent_id: mainEvent.id,
        is_public: mainEvent.is_public,
      });
      setShowSubForm(false);
      setSubForm({ name: "", event_date: "" });
      // Refresh tree and select the new sub-event
      const { data: freshMain } = await api.get(`/gallery/events/${mainEvent.id}`);
      setMainEvent(freshMain);
      setSelectedId(newSub.id);
      const { data: sub } = await api.get(`/gallery/events/${newSub.id}`);
      setSelectedNode(sub);
    } catch (e2) {
      setSubErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubSaving(false);
    }
  };

  const publicUrl = mainEvent ? `${window.location.origin}/g/${mainEvent.id}` : "";
  const copyPublicLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1800);
    } catch {
      window.prompt("Copy the public gallery link:", publicUrl);
    }
  };

  const reprocessFaces = async () => {
    if (!mainEvent) return;
    setReprocessing(true);
    setReprocessMsg("");
    try {
      const { data } = await api.post(`/gallery/events/${mainEvent.id}/reprocess-faces`, null, {
        params: { only_missing: true },
      });
      setReprocessMsg(data.queued
        ? `Queued ${data.queued} photo${data.queued === 1 ? "" : "s"} — running in the background.`
        : "All photos already processed.");
      setTimeout(() => setReprocessMsg(""), 4000);
    } catch (e) {
      setReprocessMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReprocessing(false);
    }
  };

  const qrUrl = mainEvent ? `${API}/public/gallery/${mainEvent.id}/qr` : "";

  // iter138 — Load face-index status once per event, then poll every 5 s
  // while a re-index is running so the admin card auto-updates without
  // a page reload.
  const loadIndexStatus = useCallback(async () => {
    if (!mainEvent?.id || !isAdmin) return;
    // iter140 — use Promise.allSettled so a transient failure on either
    // endpoint doesn't silently blank the whole admin panel (was
    // causing the Debug Search button + all 8 metrics to disappear
    // when /index-log 404'd on galleries with no prior job).
    const [statusRes, logRes] = await Promise.allSettled([
      api.get(`/gallery/events/${mainEvent.id}/index-status`),
      api.get(`/gallery/events/${mainEvent.id}/index-log?limit=25`),
    ]);
    if (statusRes.status === "fulfilled") {
      setIndexStatus(statusRes.value.data);
    } else {
      logger.debug("index-status fetch failed:", statusRes.reason?.message);
    }
    if (logRes.status === "fulfilled") {
      setIndexLog(logRes.value.data);
      const job = logRes.value.data?.job;
      const jobRunning = job?.status === "running" || job?.status === "queued";
      const complete = statusRes.status === "fulfilled" && statusRes.value.data.indexing_complete;
      if (complete && !jobRunning) setIndexPolling(false);
    } else {
      // Index log fetch failed — that's OK, don't blank the whole panel.
      logger.debug("index-log fetch failed:", logRes.reason?.message);
      if (statusRes.status === "fulfilled" && statusRes.value.data.indexing_complete) {
        setIndexPolling(false);
      }
    }
  }, [mainEvent?.id, isAdmin]);

  useEffect(() => {
    loadIndexStatus();
  }, [loadIndexStatus]);

  // iter139 — auto-start polling if we discover a live job on mount
  // (e.g., page refresh while indexing is running).
  useEffect(() => {
    const job = indexLog?.job;
    if (job && (job.status === "running" || job.status === "queued")) {
      setIndexPolling(true);
    }
  }, [indexLog?.job?.status, indexLog?.job]);

  useEffect(() => {
    if (!indexPolling) return;
    // iter139 — poll every 3s while a job is running so admin sees
    // live per-photo progress.
    const t = setInterval(loadIndexStatus, 3000);
    return () => clearInterval(t);
  }, [indexPolling, loadIndexStatus]);

  const runFullReindex = async () => {
    if (!mainEvent?.id) return;
    setFullReindexing(true);
    setShowReindexConfirm(false);
    try {
      const { data } = await api.post(`/gallery/events/${mainEvent.id}/reindex-full`);
      setReprocessMsg(`Rebuilding index for ${data.queued} photo${data.queued === 1 ? "" : "s"}…`);
      setIndexPolling(true);
      setTimeout(() => setReprocessMsg(""), 6000);
      // First poll immediately so the "Preparing…" chip shows.
      setTimeout(loadIndexStatus, 1200);
    } catch (e) {
      setReprocessMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setFullReindexing(false);
    }
  };

  const runVerifyIndex = async () => {
    if (!mainEvent?.id) return;
    try {
      await api.post(`/gallery/events/${mainEvent.id}/verify-index`);
      loadIndexStatus();
    } catch (e) {
      setReprocessMsg(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadZip = async () => {
    if (!mainEvent) return;
    setDownloading(true);
    try {
      // Direct-nav download so browser handles the streamed zip natively
      const link = document.createElement("a");
      link.href = `${API}/public/gallery/${mainEvent.id}/download?scope=all`;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setTimeout(() => setDownloading(false), 1200);
    }
  };

  const copyQr = async () => {
    if (!qrUrl) return;
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    } catch (e) {
      logger.debug("QR clipboard copy failed:", e?.message);
    }
  };

  if (loading || !mainEvent || !selectedNode) {
    // iter147 — Skeleton-based admin gallery loading so folder tabs
    // + photo grid stabilise without CLS.
    return (
      <div className="space-y-4" data-testid="gallery-detail-loading" aria-busy="true">
        <div className="kb-skeleton h-3 w-24" />
        <div className="flex items-baseline justify-between">
          <div className="kb-skeleton h-6 w-64 max-w-full" />
          <div className="kb-skeleton h-8 w-32 rounded-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="kb-skeleton h-8 w-24 rounded-full" />
          ))}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {Array.from({ length: 18 }).map((_, i) => (
            <div key={i} className="kb-skeleton aspect-square rounded-md" />
          ))}
        </div>
        <span className="sr-only">Loading gallery…</span>
      </div>
    );
  }

  const isMainSelected = selectedId === mainEvent.id;
  const photos = selectedNode.photos || [];
  const subEvents = mainEvent.sub_events || [];

  return (
    <div className="space-y-4 fade-in" data-testid="gallery-detail-page">
      <div>
        <Link to="/gallery" className="text-xs text-stone-500 hover:text-rose-700 inline-flex items-center gap-1" data-testid="gallery-back">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> All galleries
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-2">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">{mainEvent.name}</h1>
            <div className="text-sm text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
              {mainEvent.couple_name && <span>{mainEvent.couple_name}</span>}
              {mainEvent.event_date && <span className="font-mono">{fmtDate(mainEvent.event_date)}</span>}
              <span>{mainEvent.photo_count} photo{mainEvent.photo_count === 1 ? "" : "s"}</span>
              {mainEvent.sub_event_count > 0 && <span>· {mainEvent.sub_event_count} sub-event{mainEvent.sub_event_count === 1 ? "" : "s"}</span>}
              {mainEvent.is_public && <span className="badge badge-blue">Public</span>}
            </div>
          </div>
          {mainEvent.is_public && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="btn btn-outline btn-sm"
                onClick={copyPublicLink}
                title="Copy the public share link for guests"
                data-testid="gallery-copy-public-link"
              >
                <Share2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                {copiedLink ? "Copied!" : "Share link"}
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowQr(true)}
                title="Show a QR code that opens this gallery"
                data-testid="gallery-show-qr-btn"
              >
                <QrCode className="w-3.5 h-3.5" strokeWidth={1.8} />
                Show QR
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={downloadZip}
                disabled={downloading || (mainEvent.photo_count || 0) === 0}
                title="Download every photo in this gallery as a ZIP"
                data-testid="gallery-download-zip-btn"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={1.8} />}
                Download ZIP
              </button>
              {canWrite && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={reprocessFaces}
                  disabled={reprocessing}
                  title="Detect faces in all photos so guests can search by selfie"
                  data-testid="gallery-reprocess-faces"
                >
                  {reprocessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />}
                  Re-scan faces
                </button>
              )}
              {isAdmin && (
                <button className="btn btn-danger btn-sm" onClick={deleteMainEvent} data-testid="gallery-delete-event-btn">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete folder
                </button>
              )}
            </div>
          )}
          {!mainEvent.is_public && isAdmin && (
            <button className="btn btn-danger btn-sm" onClick={deleteMainEvent} data-testid="gallery-delete-event-btn">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete folder
            </button>
          )}
        </div>
      </div>

      {mainEvent.is_public && (publicUrl || reprocessMsg) && (
        <div className="card px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs text-stone-600" data-testid="gallery-public-info">
          <Share2 className="w-3.5 h-3.5 text-rose-700 shrink-0" strokeWidth={1.8} />
          <span className="text-stone-500">Public link:</span>
          <code className="font-mono bg-stone-50 border border-stone-200 rounded px-2 py-0.5 truncate max-w-full">{publicUrl}</code>
          <button className="text-rose-700 hover:underline" onClick={copyPublicLink} data-testid="gallery-inline-copy">{copiedLink ? "Copied!" : "Copy"}</button>
          {reprocessMsg && (
            <span className="ml-auto text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="gallery-reprocess-msg">
              {reprocessMsg}
            </span>
          )}
        </div>
      )}

      {/* iter198 — Per-gallery watermark override */}
      <GalleryWatermarkCard
        event={mainEvent}
        studioDefault={studioWatermark}
        onChange={saveGalleryWatermark}
        saving={savingWatermark}
        readOnly={!canWrite}
      />

      {/* iter138 — Face-Index Reliability panel (admin only). */}
      {isAdmin && indexStatus && (
        <FaceIndexPanel
          status={indexStatus}
          log={indexLog}
          onReindexFull={() => setShowReindexConfirm(true)}
          onVerify={runVerifyIndex}
          isBusy={fullReindexing || indexPolling}
        />
      )}

      {/* iter138 — confirmation dialog for the destructive Full Re-index */}
      {showReindexConfirm && indexStatus && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="reindex-confirm-modal"
          onClick={() => setShowReindexConfirm(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-stone-900">Rebuild face index?</h3>
                <p className="text-sm text-stone-600 mt-2">
                  This will re-scan every photo ({indexStatus.photos_uploaded} photo{indexStatus.photos_uploaded === 1 ? "" : "s"}) and rebuild the face-recognition index from scratch.
                  Existing search history is not affected. Typically completes in a few minutes.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowReindexConfirm(false)}
                data-testid="reindex-cancel-btn"
              >Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={runFullReindex}
                data-testid="reindex-confirm-btn"
              >
                Rebuild Index
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two-column layout: tree sidebar + selected node pane */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Left: Media tree */}
        <aside className="card p-3 h-fit" data-testid="gallery-tree">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-stone-500 font-semibold px-1 pb-2 border-b border-stone-100">
            <FolderTree className="w-3.5 h-3.5" strokeWidth={2} />
            Media
          </div>
          <div className="mt-2 space-y-0.5">
            {/* Main event node */}
            <button
              type="button"
              onClick={() => selectNode(mainEvent.id)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm transition-colors ${
                isMainSelected ? "bg-rose-50 text-rose-900 font-semibold" : "text-stone-700 hover:bg-stone-50"
              }`}
              data-testid={`tree-node-${mainEvent.id}`}
            >
              {isMainSelected ? <FolderOpen className="w-4 h-4 text-rose-700" strokeWidth={1.8} /> : <Folder className="w-4 h-4 text-stone-500" strokeWidth={1.8} />}
              <span className="flex-1 truncate">{mainEvent.name}</span>
              <span className="text-[10px] font-mono text-stone-400">{mainEvent.own_photo_count}</span>
            </button>

            {/* Sub-events */}
            <div className="ml-2 border-l border-stone-100 pl-2 space-y-0.5">
              {subEvents.map((s) => (
                <div key={s.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => selectNode(s.id)}
                    className={`flex-1 flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm transition-colors ${
                      selectedId === s.id ? "bg-rose-50 text-rose-900 font-semibold" : "text-stone-700 hover:bg-stone-50"
                    }`}
                    data-testid={`tree-node-${s.id}`}
                  >
                    {selectedId === s.id ? <FolderOpen className="w-3.5 h-3.5 text-rose-700" strokeWidth={1.8} /> : <Folder className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.8} />}
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="text-[10px] font-mono text-stone-400">{s.photo_count}</span>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => deleteSubEvent(s)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-stone-400 hover:text-red-700 rounded transition"
                      title={`Delete "${s.name}"`}
                      data-testid={`tree-delete-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </button>
                  )}
                </div>
              ))}
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setShowSubForm(true)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-stone-500 hover:bg-rose-50 hover:text-rose-700 transition-colors border border-dashed border-stone-200 hover:border-rose-300"
                  data-testid="add-sub-event-btn"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                  Add sub event
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Right: Selected node uploader + photo grid */}
        <div className="space-y-4 min-w-0">
          {/* Selected node header */}
          <div className="card px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <FolderOpen className="w-4 h-4 text-rose-700 shrink-0" strokeWidth={1.8} />
              <div className="min-w-0">
                <div className="font-heading font-semibold text-stone-900 text-sm truncate">
                  {isMainSelected ? mainEvent.name : selectedNode.name}
                </div>
                <div className="text-[11px] text-stone-500">
                  {isMainSelected ? "Main event · photos uploaded here won't belong to any sub-event" : `Sub-event of ${mainEvent.name}`}
                </div>
              </div>
            </div>
            <div className="text-xs text-stone-500 font-mono shrink-0">{photos.length} photo{photos.length === 1 ? "" : "s"}</div>
          </div>

          {/* Uploader */}
          {canWrite && (
            <>
              <UploadPresetBar
                preset={uploadPreset}
                onChange={changeUploadPreset}
                disabled={queueOpen}
                compressing={false}
                stats={null}
              />
              <div
                className={`card p-6 border-2 border-dashed transition-colors ${
                  dragOver ? "border-rose-500 bg-rose-50/60" : "border-stone-300 bg-stone-50/40"
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="gallery-dropzone"
              >
                <div className="flex flex-col items-center text-center">
                  <Upload className={`w-7 h-7 mb-2 ${dragOver ? "text-rose-700" : "text-stone-400"}`} strokeWidth={1.4} />
                  <div className="font-heading font-semibold text-stone-800">
                    {queueOpen
                      ? "Uploading… see progress panel"
                      : dragOver
                        ? "Drop to upload"
                        : `Drop photos into "${isMainSelected ? mainEvent.name : selectedNode.name}"`}
                  </div>
                  <div className="text-xs text-stone-500 mt-1 max-w-md">
                    JPEG, PNG, WEBP, or HEIC · up to 60 MB each · duplicates auto-skipped
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={queueOpen}
                    data-testid="gallery-browse-btn"
                  >
                    <Upload className="w-3.5 h-3.5" strokeWidth={1.6} /> Browse files
                  </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  multiple
                  className="hidden"
                  onChange={onFileInputChange}
                  data-testid="gallery-file-input"
                />
              </div>

              {uploadResults && (
                <div className="mt-4 space-y-2 text-sm">
                  {uploadResults.uploaded && uploadResults.uploaded.length > 0 && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                      <span>Uploaded {uploadResults.uploaded.length} photo{uploadResults.uploaded.length === 1 ? "" : "s"}.</span>
                      <button onClick={() => setUploadResults(null)} className="ml-auto text-green-800/60 hover:text-green-900"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                        <span className="font-semibold">{uploadResults.errors.length} file{uploadResults.errors.length === 1 ? "" : "s"} skipped</span>
                      </div>
                      <ul className="text-xs space-y-0.5 pl-6 list-disc">
                        {uploadResults.errors.slice(0, 5).map((er, i) => (
                          <li key={`err-${i}`}>{er.filename ? <span className="font-mono">{er.filename}</span> : ""}{er.filename ? " — " : ""}{er.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              </div>
            </>
          )}

          {/* iter168 — Per-file upload queue modal (portal-rendered). */}
          <GalleryUploadQueue
            open={queueOpen}
            files={queueFiles}
            eventId={selectedId}
            uploadPreset={uploadPreset}
            onBatchDone={() => selectedId && refreshSelected(selectedId)}
            onClose={() => { setQueueOpen(false); setQueueFiles(null); }}
          />

          {/* Photo grid */}
          <div>
            {photos.length === 0 ? (
              <div className="card p-10 text-center">
                <ImageOff className="w-7 h-7 text-stone-300 mx-auto mb-2" strokeWidth={1.4} />
                <div className="text-sm text-stone-500">
                  {isMainSelected && subEvents.length > 0
                    ? "No photos at the main level. Photos live inside sub-events →"
                    : "No photos yet. Drop some above to get started."}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative aspect-square group card overflow-hidden bg-stone-100" data-testid={`gallery-photo-${p.id}`}>
                    <button type="button" className="w-full h-full block" onClick={() => setLightbox(p)} aria-label={`Open ${p.filename}`}>
                      <img src={photoFileUrl(p.id)} alt={p.filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                    </button>
                    {canWrite && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePhoto(p); }}
                        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white rounded-md p-1 hover:bg-red-600"
                        title="Delete photo"
                        data-testid={`gallery-photo-delete-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={() => setLightbox(null)}>
            <X className="w-6 h-6" />
          </button>
          <img src={photoFileUrl(lightbox.id)} alt={lightbox.filename} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs font-mono bg-black/40 px-3 py-1 rounded">
            {lightbox.filename} · {(lightbox.size_bytes / 1024).toFixed(0)} KB
          </div>
        </div>
      )}

      {/* Add sub-event modal */}
      {showSubForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 fade-in" onClick={() => setShowSubForm(false)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="new-sub-event-modal">
            <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-rose-700" strokeWidth={2} />
                <h2 className="font-heading font-semibold">Add sub event to &ldquo;{mainEvent.name}&rdquo;</h2>
              </div>
              <button className="btn-ghost p-2 rounded" onClick={() => setShowSubForm(false)}><X className="w-5 h-5" strokeWidth={1.6} /></button>
            </div>
            <form onSubmit={createSubEvent} className="p-6 space-y-3">
              <div>
                <label className="label">Sub event name *</label>
                <input required autoFocus className="input" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} placeholder="Haldi / Mehendi / Day 01" data-testid="sub-form-name" />
              </div>
              <div>
                <label className="label">Sub event date (optional)</label>
                <input type="date" className="input" value={subForm.event_date} onChange={(e) => setSubForm({ ...subForm, event_date: e.target.value })} data-testid="sub-form-date" />
              </div>
              {subErr && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{subErr}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={() => setShowSubForm(false)} disabled={subSaving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={subSaving} data-testid="sub-form-save">
                  {subSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.6} />}
                  Create sub event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR modal */}
      {showQr && mainEvent && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)} data-testid="gallery-qr-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowQr(false)}
              className="absolute top-2 right-2 text-stone-500 hover:text-stone-900 p-1"
              data-testid="gallery-qr-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[11px] uppercase tracking-[0.25em] text-rose-700 font-semibold">Kahani gallery</div>
            <h3 className="font-heading text-xl font-semibold text-stone-900 mt-1">{mainEvent.couple_name || mainEvent.name}</h3>
            <p className="text-xs text-stone-500 mt-1">Guests can scan to open the album — no login required.</p>
            <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3 flex items-center justify-center">
              <img
                src={qrUrl}
                alt={`QR code for ${mainEvent.name}`}
                className="w-64 h-64 object-contain"
                data-testid="gallery-qr-image"
              />
            </div>
            <code className="mt-3 block text-[11px] font-mono text-stone-500 truncate">{publicUrl}</code>
            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <button className="btn btn-outline btn-sm" onClick={copyQr} data-testid="gallery-qr-copy">
                Copy image
              </button>
              <a
                className="btn btn-primary btn-sm"
                href={qrUrl}
                download={`kahani-qr-${(mainEvent.couple_name || mainEvent.name || "gallery").replace(/[^A-Za-z0-9-]+/g, "-")}.png`}
                data-testid="gallery-qr-download"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.8} /> Download PNG
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadPresetBar({ preset, onChange, disabled, compressing, stats }) {
  const options = [
    { key: "original", label: "Original", icon: Gem,     tint: "text-amber-600" },
    { key: "balanced", label: "Balanced", icon: Feather, tint: "text-rose-700" },
    { key: "fast",     label: "Fast",     icon: Zap,     tint: "text-emerald-600" },
  ];
  const activeMeta = COMPRESSION_PRESETS[preset] || COMPRESSION_PRESETS.balanced;
  const savedPct = stats && stats.before > 0 ? Math.max(0, Math.round((1 - stats.after / stats.before) * 100)) : null;
  return (
    <div className="card p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="upload-preset-bar">
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Upload quality</div>
      </div>
      <div className="inline-flex rounded-lg border border-stone-200 overflow-hidden bg-stone-50" role="tablist" aria-label="Upload quality preset">
        {options.map((o) => {
          const active = preset === o.key;
          const Icon = o.icon;
          return (
            <button
              key={o.key}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.key)}
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors border-r border-stone-200 last:border-r-0 ${
                active ? "bg-white text-stone-900" : "text-stone-500 hover:text-stone-800"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`upload-preset-${o.key}`}
              aria-pressed={active}
            >
              <Icon className={`w-3.5 h-3.5 ${active ? o.tint : "text-stone-400"}`} strokeWidth={1.8} />
              {o.label}
            </button>
          );
        })}
      </div>
      <div className="text-xs text-stone-500 min-w-0 flex-1 truncate">{activeMeta.hint}</div>
      {compressing && (
        <span className="text-xs text-rose-700 inline-flex items-center gap-1 shrink-0">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Optimising…
        </span>
      )}
      {!compressing && stats && (
        <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0" data-testid="upload-compress-savings">
          {humanBytes(stats.before)} → {humanBytes(stats.after)} · saved {savedPct}%
        </span>
      )}
    </div>
  );
}


// iter138 — Admin-only Face-Index Reliability panel.
// Renders the 7 requested metrics (Uploaded, Indexed, Faces, Ready,
// Last Indexed, Last Re-indexed By, Avg Faces/Photo) plus the actions
// (Verify index + Rebuild index). Uses only lucide-react icons and
// existing Tailwind tokens — matches surrounding gallery UI so no
// design guidelines are broken.
function FaceIndexPanel({ status, log, onReindexFull, onVerify, isBusy }) {
  const {
    photos_uploaded = 0,
    photos_indexed = 0,
    faces_detected = 0,
    average_faces_per_photo = 0,
    indexing_complete,
    progress_percent = 0,
    search_ready = "",
    last_indexed_at = "",
    last_reindexed_by = "",
    last_reindexed_at = "",
    verification,
  } = status || {};

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const readyPill = (status?.search_ready_bool ?? indexing_complete) ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-medium">
      <CheckCircle2 className="w-3 h-3" /> Search Ready
    </span>
  ) : status?.stalled ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200 text-[11px] font-medium">
      <AlertCircle className="w-3 h-3" /> Stalled · {status?.embedding_coverage_pct ?? 0}%
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-medium">
      <Loader2 className="w-3 h-3 animate-spin" /> Preparing… {progress_percent}%
    </span>
  );

  return (
    <div
      className="card px-4 py-3.5 space-y-3"
      data-testid="face-index-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
          <span className="font-semibold text-sm text-stone-900">Find My Photos · Index Health</span>
          {readyPill}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="debug-search"
            className="btn btn-outline btn-sm"
            title="Admin-only: run a single search with full diagnostic output"
            data-testid="face-index-debug-btn"
          >
            Debug Search
          </Link>
          <button
            className="btn btn-outline btn-sm"
            onClick={onVerify}
            title="Randomly sample the index and verify embeddings"
            data-testid="face-index-verify-btn"
          >
            Verify sample
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={onReindexFull}
            disabled={isBusy || photos_uploaded === 0}
            title="Wipe and rebuild the face index for every photo in this gallery"
            data-testid="face-index-reindex-btn"
          >
            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Re-index Gallery
          </button>
        </div>
      </div>

      {!indexing_complete && photos_uploaded > 0 && (
        <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden" data-testid="face-index-progress">
          <div
            className="h-1.5 bg-amber-500 transition-all"
            style={{ width: `${Math.min(100, progress_percent)}%` }}
          />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <IndexMetric label="Photos Uploaded" value={photos_uploaded} testid="metric-uploaded" />
        <IndexMetric label="Photos Indexed" value={photos_indexed} testid="metric-indexed" />
        <IndexMetric label="With Embeddings" value={status?.photos_with_embeddings ?? 0} testid="metric-embeddings" />
        <IndexMetric label="Unindexed" value={status?.unindexed_photos ?? 0} testid="metric-unindexed" />
        <IndexMetric label="Faces Detected" value={faces_detected} testid="metric-faces" />
        <IndexMetric label="Coverage" value={`${(status?.embedding_coverage_pct ?? 0)}%`} testid="metric-coverage" />
        <IndexMetric label="Search Ready" value={search_ready || "—"} testid="metric-ready" />
        <IndexMetric label="Job Status" value={status?.job_status || "none"} testid="metric-job-status" />
        <IndexMetric label="Last Indexed" value={fmt(last_indexed_at)} testid="metric-last-indexed" />
        <IndexMetric label="Last Re-indexed By" value={last_reindexed_by || "—"} testid="metric-reindexed-by" />
        <IndexMetric label="Last Re-index Run" value={fmt(last_reindexed_at)} testid="metric-reindexed-at" />
        <IndexMetric label="Avg Faces / Photo" value={Number(average_faces_per_photo || 0).toFixed(2)} testid="metric-avg-faces" />
      </div>

      {verification && verification.status && verification.status !== "empty" && (
        <div
          className={`text-xs rounded-md px-3 py-2 border ${
            verification.status === "healthy"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : verification.status === "warning"
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-rose-50 text-rose-800 border-rose-200"
          }`}
          data-testid="face-index-verification"
        >
          <div className="font-medium">
            {verification.status === "healthy"
              ? "Sample verification passed"
              : verification.status === "warning"
                ? "Sample verification: some photos need attention"
                : "Sample verification: index errors detected"}
          </div>
          <div className="mt-0.5 text-[11px]">
            Sampled {verification.sample_size} of {verification.total_processed} · embeddings OK: {verification.embeddings_ok}
            {verification.corrupted?.length > 0 && ` · flagged: ${verification.corrupted.length}`}
            {verification.run_at && ` · ${fmt(verification.run_at)}`}
          </div>
          {verification.corrupted?.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 max-h-24 overflow-y-auto">
              {verification.corrupted.slice(0, 5).map((c) => (
                <li key={c.id} className="text-[11px] font-mono truncate" data-testid={`verify-corrupt-${c.id}`}>
                  · {c.filename || c.id} — {(c.reasons || []).join(", ")}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* iter139 — Live Index Log (admin-only). Shows the durable job
          state so ops can immediately see: currently-processing file,
          processed/failed counters, last exception, ETA. */}
      {log?.job && (
        <IndexLogBlock job={log.job} logs={log.logs || []} />
      )}
    </div>
  );
}

function IndexLogBlock({ job, logs }) {
  const {
    status, total = 0, processed = 0, failed = 0,
    current_filename = "", current_position = null,
    last_error = null, last_error_at = null, last_error_filename = null,
    avg_photo_ms = 0, estimated_completion_at = "", started_by = "",
  } = job || {};
  const active = status === "running" || status === "queued";
  const done = processed + failed;

  const fmtEta = () => {
    if (!estimated_completion_at) return "—";
    try {
      const target = new Date(estimated_completion_at).getTime();
      const secs = Math.max(0, Math.round((target - Date.now()) / 1000));
      if (secs < 60) return `~${secs}s`;
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `~${m}m ${s}s`;
    } catch { return "—"; }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return iso; }
  };

  const pillColor = status === "completed"
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : status === "failed"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : "bg-amber-50 text-amber-800 border-amber-200";

  return (
    <div className="pt-3 border-t border-stone-200 space-y-2" data-testid="index-log-block">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">Index Log</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${pillColor}`} data-testid="index-log-status">
            {active && <Loader2 className="w-3 h-3 animate-spin" />}
            {status || "unknown"}
          </span>
          {started_by && (
            <span className="text-[11px] text-stone-500">by {started_by}</span>
          )}
        </div>
        <div className="text-[11px] font-mono text-stone-600" data-testid="index-log-counters">
          <span className="text-emerald-700">✓ {processed}</span>
          <span className="mx-1.5 text-stone-400">·</span>
          <span className="text-rose-700">✗ {failed}</span>
          <span className="mx-1.5 text-stone-400">·</span>
          <span>{done}/{total}</span>
          {avg_photo_ms > 0 && (
            <>
              <span className="mx-1.5 text-stone-400">·</span>
              <span>avg {avg_photo_ms}ms</span>
            </>
          )}
          {active && estimated_completion_at && (
            <>
              <span className="mx-1.5 text-stone-400">·</span>
              <span>ETA {fmtEta()}</span>
            </>
          )}
        </div>
      </div>

      {active && current_filename && (
        <div className="text-[12px] text-stone-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 flex items-center gap-2 truncate" data-testid="index-log-current">
          <Loader2 className="w-3 h-3 animate-spin text-amber-600 shrink-0" />
          <span className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Now processing</span>
          <span className="font-mono truncate">{current_filename}</span>
          {current_position != null && total > 0 && (
            <span className="text-stone-500 shrink-0">· {current_position} / {total}</span>
          )}
        </div>
      )}

      {last_error && (
        <div className="text-[11px] rounded bg-rose-50 border border-rose-200 text-rose-800 px-3 py-1.5" data-testid="index-log-last-error">
          <span className="font-semibold">Last error</span>
          {last_error_filename && <span className="text-rose-600 ml-1">on {last_error_filename}</span>}
          {last_error_at && <span className="text-rose-600 ml-1">@ {fmtTime(last_error_at)}</span>}
          <div className="font-mono text-[10.5px] mt-0.5 truncate">{last_error}</div>
        </div>
      )}

      {logs.length > 0 && (
        <details className="text-[11px]" data-testid="index-log-details">
          <summary className="cursor-pointer text-stone-500 hover:text-stone-800 select-none">
            Show last {Math.min(logs.length, 25)} photo{logs.length === 1 ? "" : "s"}
          </summary>
          <div className="mt-2 border border-stone-200 rounded overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-[10.5px] font-mono">
              <thead className="bg-stone-50 text-stone-500 text-[9.5px] uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-1 text-left">#</th>
                  <th className="px-2 py-1 text-left">File</th>
                  <th className="px-2 py-1 text-right">Faces</th>
                  <th className="px-2 py-1 text-right">DL</th>
                  <th className="px-2 py-1 text-right">Embed</th>
                  <th className="px-2 py-1 text-right">Dur</th>
                  <th className="px-2 py-1 text-right">RSS</th>
                  <th className="px-2 py-1 text-center">OK</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((r) => (
                  <tr key={r.id || `${r.job_id}-${r.position}`} className="border-t border-stone-100" data-testid={`index-log-row-${r.position}`}>
                    <td className="px-2 py-0.5 text-stone-500">{r.position}</td>
                    <td className="px-2 py-0.5 truncate max-w-[180px]" title={r.filename}>{r.filename}</td>
                    <td className="px-2 py-0.5 text-right">{r.detected_faces}</td>
                    <td className="px-2 py-0.5 text-right text-stone-500">{r.download_ms}ms</td>
                    <td className="px-2 py-0.5 text-right text-stone-500">{r.embed_ms}ms</td>
                    <td className="px-2 py-0.5 text-right">{r.duration_ms}ms</td>
                    <td className="px-2 py-0.5 text-right text-stone-500">{r.rss_mb ? `${r.rss_mb}M` : "—"}</td>
                    <td className={`px-2 py-0.5 text-center ${r.success ? "text-emerald-700" : "text-rose-700"}`} title={r.error || ""}>
                      {r.success ? "✓" : "✗"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function IndexMetric({ label, value, testid }) {
  return (
    <div className="bg-stone-50/70 rounded-md px-3 py-2 border border-stone-100">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">{label}</div>
      <div className="text-sm font-semibold text-stone-900 truncate" data-testid={testid}>{value}</div>
    </div>
  );
}


// ============ iter198 — Per-gallery Watermark override card ============
function GalleryWatermarkCard({ event, studioDefault, onChange, saving, readOnly }) {
  if (!event) return null;
  const override = event.watermark_enabled;
  const inheriting = override === null || override === undefined;
  const studioOn = !!(studioDefault && studioDefault.enabled);
  const effective = inheriting ? studioOn : !!override;

  const OPTIONS = [
    { key: "inherit", label: "Use studio default", value: null, hint: studioDefault ? (studioOn ? "Currently ON" : "Currently OFF") : "Studio default" },
    { key: "on",      label: "Force ON",           value: true, hint: "Watermark every photo" },
    { key: "off",     label: "Force OFF",          value: false, hint: "No watermark anywhere" },
  ];
  const selected = inheriting ? "inherit" : (override ? "on" : "off");

  return (
    <section
      className="rounded-2xl border border-stone-200 bg-white shadow-sm p-5 sm:p-6"
      data-testid="gallery-watermark-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`rounded-xl p-2 ${effective ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-500"}`}>
            <Stamp className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Watermark</div>
            <div className="font-heading text-lg sm:text-xl font-semibold text-stone-900 mt-0.5">
              {effective ? "Protecting every photo in this gallery" : "Photos delivered without watermark"}
            </div>
            <div className="text-xs text-stone-500 mt-1 max-w-2xl">
              Applied consistently across viewing, individual downloads, and ZIP downloads.
              Change the studio-wide default in <span className="font-semibold">Settings → Media</span>.
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
          effective ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-600 border border-stone-200"
        }`} data-testid="gallery-watermark-effective">
          <span className={`w-1.5 h-1.5 rounded-full ${effective ? "bg-emerald-600" : "bg-stone-400"}`} />
          {effective ? "ON" : "OFF"}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="gallery-watermark-options">
        {OPTIONS.map((o) => {
          const active = selected === o.key;
          return (
            <button
              key={o.key}
              type="button"
              disabled={readOnly || saving}
              onClick={() => onChange(o.value)}
              className={`text-left rounded-xl border p-3 transition-colors ${
                active
                  ? "border-stone-900 bg-stone-900 text-white"
                  : "border-stone-200 bg-white hover:bg-stone-50 text-stone-900"
              } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`gallery-watermark-opt-${o.key}`}
            >
              <div className="text-sm font-semibold flex items-center gap-2">
                {o.label}
                {active && saving && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
              <div className={`text-[11px] mt-0.5 ${active ? "text-stone-300" : "text-stone-500"}`}>
                {o.hint}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
